import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/lib/auth";
import { usePersona } from "@/contexts/persona-context";
import { Link, useLocation } from "wouter";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { MapPin, Calendar, Filter, ShoppingCart, Mic2, Search, X, Target, Sparkles, TrendingUp, Users } from "lucide-react";
import { type ArtistWithShows, type ShowWithDetails, type PaginatedResult } from "@shared/schema";
import { PlanStorage } from "@/lib/plan-storage";
import { usePlanSidebar } from "@/contexts/plan-sidebar-context";
import { type AdvancedFilters, AdvancedFilterContent } from "@/components/advanced-filter";
import {
  applyHardFilters,
  applyAudienceFiltersToArtists,
  getDefaultAdvancedFilters,
  type ArtistWithMatch
} from "@/lib/audience-matching";
import Navbar from "@/components/navbar";
import { UnifiedFilterBar } from "@/components/unified-filter-bar";
import ArtistCard from "@/components/artist-card";
import ArtistModal from "@/components/artist-modal";
import { Pagination } from "@/components/pagination";
import { useToast } from "@/hooks/use-toast";
import { BrowseLayout } from "@/components/layouts/browse-layout";
import { apiRequest } from "@/lib/queryClient";


export default function BrowseArtists() {
  const { user } = useAuth();
  const { activePersona } = usePersona();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [location] = useLocation();
  const [selectedArtist, setSelectedArtist] = useState<ArtistWithShows | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedShows, setSelectedShows] = useState<ShowWithDetails[]>([]);
  const { open: openPlanSidebar } = usePlanSidebar();
  const [showAuthModal, setShowAuthModal] = useState(false);

  const [advancedFilters, setAdvancedFilters] = useState<AdvancedFilters>(() => {
    const params = new URLSearchParams(window.location.search);
    const careerStageParam = params.get("careerStage");
    const venueTypeParam = params.get("venueType");
    return {
      ...getDefaultAdvancedFilters(),
      artistName: params.get("name") || params.get("artistName") || "",
      venueName: params.get("venueName") || "",
      careerStage: careerStageParam ? careerStageParam.split(',') : [],
      venueType: venueTypeParam ? venueTypeParam.split(',') : [],
      setArtist: params.get("setArtist") || "",
      location: params.get("location") || "",
      genre: params.get("genre") ? [params.get("genre")!] : [],
      dateFrom: params.get("dateFrom") || "",
      dateTo: params.get("dateTo") || "",
    };
  });

  // Apply active persona filters
  useEffect(() => {
    if (activePersona) {
      const personaFilters: AdvancedFilters = {
        ...advancedFilters,
        targetAudience: {
          fanGender: activePersona.genders || [],
          fanEthnicity: activePersona.ethnicities || [],
          fanAge: activePersona.ageRanges || [],
          fanIncome: activePersona.incomeLevels || [],
          fanAttributes: activePersona.lifestyleAttributes || [],
        },
        // NOTE: targetLocations are now converted to DMA IDs for hard filtering
        // They no longer contribute to location string or percentage match
        // Add required fields for hard filtering
        ...(activePersona.requiredAgeRanges || activePersona.requiredGenders || activePersona.requiredEthnicities ||
           activePersona.requiredIncomeLevels || activePersona.requiredLifestyleAttributes ||
           activePersona.requiredMusicGenres || activePersona.requiredTargetLocations
          ? {
              requiredFields: {
                requiredAgeRanges: activePersona.requiredAgeRanges,
                requiredGenders: activePersona.requiredGenders,
                requiredEthnicities: activePersona.requiredEthnicities,
                requiredIncomeLevels: activePersona.requiredIncomeLevels,
                requiredTargetLocations: activePersona.requiredTargetLocations,
                requiredLifestyleAttributes: activePersona.requiredLifestyleAttributes,
                requiredMusicGenres: activePersona.requiredMusicGenres,
              }
            }
          : {}),
      };
      setAdvancedFilters(personaFilters as any);
    } else {
      // Clear audience filters when persona is removed
      const clearedFilters = {
        ...advancedFilters,
        targetAudience: {
          fanGender: [],
          fanEthnicity: [],
          fanAge: [],
          fanIncome: [],
          fanAttributes: [],
        },
      };
      setAdvancedFilters(clearedFilters);
    }
  }, [activePersona]);

  // Debounced search filters - derived from advancedFilters for API calls
  const [searchFilters, setSearchFilters] = useState(() => ({
    genre: advancedFilters.genre?.[0] || "",
    location: advancedFilters.location || "",
    dateFrom: advancedFilters.dateFrom || "",
    dateTo: advancedFilters.dateTo || "",
  }));
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const pageParam = params.get("page");
    return pageParam ? parseInt(pageParam, 10) : 1;
  });
  const [itemsPerPage] = useState(20);
  const isInitialMount = useRef(true);

  // Delay turning off initial mount protection to allow all initialization to settle
  // (persona context, debounced filters, etc. all trigger after mount)
  useEffect(() => {
    const timer = setTimeout(() => {
      isInitialMount.current = false;
    }, 500);
    return () => clearTimeout(timer);
  }, []);


  // Detect openPersona parameter - redirect to persona page or show auth modal
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const shouldOpenPersona = params.get("openPersona") === "true";

    if (shouldOpenPersona) {
      if (user) {
        // User is logged in - redirect to persona creation page
        const url = new URL(window.location.href);
        url.searchParams.delete('openPersona');
        window.history.replaceState({}, '', url.pathname + (url.search || ''));
        window.location.href = '/persona/new';
      } else {
        // User is not logged in - show auth modal
        setShowAuthModal(true);
      }
    } else {
      setShowAuthModal(false);
    }
  }, [user]);

  // Load selected shows from localStorage on mount
  useEffect(() => {
    const storedShows = PlanStorage.getSelectedShows(user?.id);
    setSelectedShows(storedShows);
  }, [user?.id]);

  // Listen for proposal storage changes from modals
  useEffect(() => {
    const handleStorageChange = () => {
      const storedShows = PlanStorage.getSelectedShows(user?.id);
      setSelectedShows(storedShows);
    };

    window.addEventListener('proposal-storage-changed', handleStorageChange);
    return () => window.removeEventListener('proposal-storage-changed', handleStorageChange);
  }, [user?.id]);



  // Track browse artists page visit for achievements
  useEffect(() => {
    if (user) {
      const trackPageVisit = async () => {
      };
      trackPageVisit();
    }
  }, [user]);

  // Debounce filters for live search while preventing focus loss during typing
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchFilters({
        genre: advancedFilters.genre?.[0] || "",
        location: advancedFilters.location || "",
        dateFrom: advancedFilters.dateFrom || "",
        dateTo: advancedFilters.dateTo || "",
      });
    }, 300); // 300ms debounce

    return () => clearTimeout(timer);
  }, [advancedFilters.genre, advancedFilters.location, advancedFilters.dateFrom, advancedFilters.dateTo]);

  // Sync basic filters with URL parameters
  useEffect(() => {
    // Start with existing URL parameters to preserve non-filter params like openPersona
    const params = new URLSearchParams(window.location.search);

    // Update or remove filter parameters
    if (searchFilters.genre) {
      params.set('genre', searchFilters.genre);
    } else {
      params.delete('genre');
    }

    if (searchFilters.location) {
      params.set('location', searchFilters.location);
    } else {
      params.delete('location');
    }

    if (searchFilters.dateFrom) {
      params.set('dateFrom', searchFilters.dateFrom);
    } else {
      params.delete('dateFrom');
    }

    if (searchFilters.dateTo) {
      params.set('dateTo', searchFilters.dateTo);
    } else {
      params.delete('dateTo');
    }

    // Preserve advanced filters in URL (artist name, venue name, career stage)
    if (advancedFilters.artistName) {
      params.set('name', advancedFilters.artistName);
    } else {
      params.delete('name');
    }

    if (advancedFilters.venueName) {
      params.set('venueName', advancedFilters.venueName);
    } else {
      params.delete('venueName');
    }

    if (advancedFilters.careerStage && advancedFilters.careerStage.length > 0) {
      params.set('careerStage', advancedFilters.careerStage.join(','));
    } else {
      params.delete('careerStage');
    }

    if (advancedFilters.venueType && advancedFilters.venueType.length > 0) {
      params.set('venueType', advancedFilters.venueType.join(','));
    } else {
      params.delete('venueType');
    }

    if (advancedFilters.setArtist) {
      params.set('setArtist', advancedFilters.setArtist);
    } else {
      params.delete('setArtist');
    }

    // Sync page number to URL (only if > 1 to keep URLs clean)
    if (currentPage > 1) {
      params.set('page', currentPage.toString());
    } else {
      params.delete('page');
    }

    const queryString = params.toString();
    const newUrl = `/browse-artists${queryString ? `?${queryString}` : ''}`;

    // Only update URL if it's different to avoid unnecessary history entries
    if (window.location.pathname + window.location.search !== newUrl) {
      window.history.replaceState({}, '', newUrl);
    }
  }, [searchFilters, advancedFilters.artistName, advancedFilters.venueName, advancedFilters.careerStage, advancedFilters.venueType, advancedFilters.setArtist, currentPage]);

  // Reset page to 1 when filters change (but not during initial mount period)
  useEffect(() => {
    if (isInitialMount.current) {
      return;
    }
    setCurrentPage(1);
  }, [searchFilters, advancedFilters.artistName, advancedFilters.venueName, advancedFilters.careerStage, advancedFilters.venueType, advancedFilters.setArtist, activePersona]);

  // Always show sticky bar when there are selected shows (no scroll handler needed)

  const { data: paginatedData, isLoading } = useQuery({
    queryKey: ["/api/artists-with-shows/paginated", searchFilters, advancedFilters.artistName, advancedFilters.venueName, advancedFilters.careerStage, advancedFilters.venueType, advancedFilters.setArtist, currentPage, itemsPerPage, activePersona],
    queryFn: async () => {
      const params = new URLSearchParams();
      // Artist name is handled by advanced filters only
      if (advancedFilters.artistName) params.append('name', advancedFilters.artistName);
      if (advancedFilters.venueName) params.append('venueName', advancedFilters.venueName);
      if (searchFilters.genre) params.append('genre', searchFilters.genre);
      if (searchFilters.location) params.append('location', searchFilters.location);
      if (searchFilters.dateFrom) params.append('dateFrom', searchFilters.dateFrom);
      if (searchFilters.dateTo) params.append('dateTo', searchFilters.dateTo);

      // Add career stage filter if selected
      if (advancedFilters.careerStage && advancedFilters.careerStage.length > 0) {
        params.append('careerStage', advancedFilters.careerStage.join(','));
      }

      // Add venue type filter if selected
      if (advancedFilters.venueType && advancedFilters.venueType.length > 0) {
        params.append('venueType', advancedFilters.venueType.join(','));
      }

      // Add SET Artist filter if selected
      if (advancedFilters.setArtist) {
        params.append('setArtist', advancedFilters.setArtist);
      }

      // Add persona filters if active persona has them
      if (user && activePersona) {
        const hasPersonaFilters = Object.values({
          fanGender: activePersona.genders || [],
          fanEthnicity: activePersona.ethnicities || [],
          fanAge: activePersona.ageRanges || [],
          fanIncome: activePersona.incomeLevels || [],
          fanAttributes: activePersona.lifestyleAttributes || [],
        }).some(arr => arr.length > 0);

        if (hasPersonaFilters) {
          const personaFilters = {
            fanGender: activePersona.genders || [],
            fanEthnicity: activePersona.ethnicities || [],
            fanAge: activePersona.ageRanges || [],
            fanIncome: activePersona.incomeLevels || [],
            fanAttributes: activePersona.lifestyleAttributes || [],
          };
          params.append('personaFilters', JSON.stringify(personaFilters));
        }

        // Add DMA IDs for hard filtering (resolved from targetLocations in persona context)
        if (activePersona.targetDmaIds && activePersona.targetDmaIds.length > 0) {
          console.log('[BROWSE-ARTISTS] Sending targetDmaIds to backend:', activePersona.targetDmaIds);
          params.append('targetDmaIds', JSON.stringify(activePersona.targetDmaIds));
        } else {
          console.log('[BROWSE-ARTISTS] No targetDmaIds found on activePersona:', activePersona);
        }
      }
      
      params.append('page', currentPage.toString());
      params.append('limit', itemsPerPage.toString());

      const url = `/api/artists-with-shows/paginated?${params}`;
      console.log('[BROWSE-ARTISTS] Fetching URL:', url);
      console.log('[BROWSE-ARTISTS] Full params object:', Object.fromEntries(params.entries()));

      const response = await fetch(url);
      if (!response.ok) {
        throw new Error('Failed to fetch artists');
      }
      return response.json() as Promise<PaginatedResult<ArtistWithShows>>;
    },
    enabled: true,
  });

  // Extract data from paginated response
  const artists = paginatedData?.items || [];
  const totalItems = paginatedData?.totalItems || 0;
  const totalShows = paginatedData?.totalShows || 0;
  const totalPages = paginatedData?.totalPages || 0;
  const hasNextPage = paginatedData?.hasNextPage || false;
  const hasPreviousPage = paginatedData?.hasPreviousPage || false;

  // Use the artists directly from the backend - they're already filtered and sorted
  const filteredArtists = useMemo(() => {
    if (!artists.length) return [];
    
    // The backend now handles persona matching and sorting
    // We just need to ensure the matchPercentage field exists for components that use it
    return artists.map(artist => ({
      ...artist,
      matchPercentage: (artist as any).audienceMatchPercentage || 0
    }));
  }, [artists]);



  const handleBasicFilterChange = (key: string, value: string) => {
    const actualValue = value === "all" ? "" : value;
    // Update advancedFilters - handle genre as array
    if (key === 'genre') {
      setAdvancedFilters(prev => ({ ...prev, genre: actualValue ? [actualValue] : [] }));
    } else {
      setAdvancedFilters(prev => ({ ...prev, [key]: actualValue }));
    }
  };

  const handleBasicSearch = async () => {
    // Track search activity for achievements

    // Search is now live via debouncing - this just tracks the search activity
  };

  const handleClearBasicFilters = () => {
    // Reset to first page when clearing filters
    setCurrentPage(1);

    // Reset all filters to defaults (single source of truth)
    const clearedFilters = getDefaultAdvancedFilters();
    setAdvancedFilters(clearedFilters);

    // Clear URL parameters
    window.history.pushState({}, "", `/browse-artists`);
  };

  const handleAdvancedFiltersChange = useCallback(async (filters: AdvancedFilters) => {
    // Single source of truth - just update advancedFilters
    setAdvancedFilters(filters);

    // Close advanced filters dialog
    setShowAdvancedFilters(false);
  }, []);



  const handleViewArtist = (artist: ArtistWithShows) => {
    setSelectedArtist(artist);
    setShowModal(true);
  };

  const handleAddToProposal = (show: ShowWithDetails) => {
    const wasAdded = PlanStorage.addShow(show, user?.id);
    if (wasAdded) {
      const updatedShows = PlanStorage.getSelectedShows(user?.id);
      setSelectedShows(updatedShows);

      // Open the plan sidebar when a show is added
      openPlanSidebar();

      toast({
        title: "Show added to proposal",
        description: `${show.artist.name} at ${show.venue.name} has been added to your proposal.`,
      });
    } else {
      toast({
        title: "Show already in proposal",
        description: `${show.artist.name} at ${show.venue.name} is already in your proposal.`,
        variant: "destructive",
      });
    }
    setShowModal(false);
  };

  const handleBulkAddToProposal = (shows: ShowWithDetails[]) => {
    let addedCount = 0;
    let duplicateCount = 0;
    
    shows.forEach(show => {
      const wasAdded = PlanStorage.addShow(show, user?.id);
      if (wasAdded) {
        addedCount++;
      } else {
        duplicateCount++;
      }
    });
    
    const updatedShows = PlanStorage.getSelectedShows(user?.id);
    setSelectedShows(updatedShows);
    
    if (addedCount > 0) {
      // Open the plan sidebar when shows are added
      openPlanSidebar();
    }
    
    if (addedCount > 0 && duplicateCount === 0) {
      toast({
        title: "Shows added to proposal",
        description: `${addedCount} show${addedCount === 1 ? '' : 's'} added to your proposal.`,
      });
    } else if (addedCount > 0 && duplicateCount > 0) {
      toast({
        title: "Shows processed",
        description: `${addedCount} show${addedCount === 1 ? '' : 's'} added, ${duplicateCount} already in proposal.`,
      });
    } else {
      toast({
        title: "No shows added",
        description: "All selected shows are already in your proposal.",
        variant: "destructive",
      });
    }
    
    // Trigger a page refresh or state update to reflect the change in the sticky bar
    window.dispatchEvent(new Event('proposal-storage-changed'));
    setShowModal(false);
  };

  // Remove authentication check - allow basic search for all users

  // Handler for unified filter bar changes - updates single source of truth
  const handleUnifiedFilterChange = useCallback((key: string, value: string | string[]) => {
    setAdvancedFilters(prev => {
      // Handle genre: convert string to string[] for consistency
      if (key === 'genre' && typeof value === 'string') {
        return { ...prev, genre: value ? [value] : [] };
      }
      return { ...prev, [key]: value };
    });
  }, []);

  return (
    <BrowseLayout>
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      {/* Unified Filter Bar */}
      <UnifiedFilterBar
        currentMethod="artists"
        filters={{
          location: advancedFilters.location,
          genre: advancedFilters.genre?.[0] || '',  // Convert string[] to string for display
          dateFrom: advancedFilters.dateFrom,
          dateTo: advancedFilters.dateTo,
          artistName: advancedFilters.artistName,
          venueName: advancedFilters.venueName,
          careerStage: advancedFilters.careerStage,
          venueType: advancedFilters.venueType,
          setArtist: advancedFilters.setArtist,
        }}
        onFilterChange={handleUnifiedFilterChange}
        onClearFilters={handleClearBasicFilters}
        onAdvancedFilters={() => setShowAdvancedFilters(true)}
        isAuthenticated={!!user}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 pt-8">

        {/* Advanced Filters Dialog */}
        {showAdvancedFilters && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
            onClick={() => setShowAdvancedFilters(false)}
          >
            <div 
              className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto m-4"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold">Advanced Filters</h2>
                <button
                  onClick={() => setShowAdvancedFilters(false)}
                  className="rounded-full opacity-80 ring-offset-background transition-all hover:opacity-100 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none bg-background/90 backdrop-blur-sm border shadow-lg p-1.5 hover:shadow-xl"
                >
                  <X className="h-4 w-4" />
                  <span className="sr-only">Close</span>
                </button>
              </div>
              <AdvancedFilterContent 
                key={JSON.stringify(advancedFilters)}
                currentFilters={advancedFilters}
                onFiltersChange={handleAdvancedFiltersChange}
                browsePage="artists"
              />
            </div>
          </div>
        )}

        {/* Results Summary */}
        {!isLoading && (
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
              <Mic2 className="h-4 w-4" />
              <span>
                {totalItems} artist{totalItems !== 1 ? 's' : ''} and {totalShows} show{totalShows !== 1 ? 's' : ''} found
              </span>
            </div>
          </div>
        )}

        {/* Artist Results */}
        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[...Array(8)].map((_, i) => (
              <Card key={i} className="animate-pulse">
                <div className="aspect-[3/2] bg-gray-200 rounded-t-lg"></div>
                <CardContent className="p-4">
                  <div className="h-4 bg-gray-200 rounded mb-2"></div>
                  <div className="h-3 bg-gray-200 rounded w-3/4 mb-4"></div>
                  <div className="h-10 bg-gray-200 rounded"></div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : filteredArtists.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-gray-400 mb-4">
              <Filter className="w-12 h-12 mx-auto mb-4" />
              <p className="text-lg font-semibold">No artists found</p>
              <p className="text-sm">Try adjusting your search filters</p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
            {filteredArtists.map((artist) => (
              <ArtistCard
                key={artist.id}
                artist={artist}
                onViewArtist={handleViewArtist}
                isAuthenticated={!!user}
                currentFilters={{
                  genre: advancedFilters.genre,
                  location: advancedFilters.location,
                  dateFrom: advancedFilters.dateFrom,
                  dateTo: advancedFilters.dateTo,
                  artistName: advancedFilters.artistName,
                  venueName: advancedFilters.venueName,
                  targetAudience: advancedFilters.targetAudience,
                  minMatchPercentage: advancedFilters.minMatchPercentage,
                }}
              />
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-8">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalItems}
              itemsPerPage={itemsPerPage}
              onPageChange={setCurrentPage}
            />
          </div>
        )}

        <ArtistModal
          artist={selectedArtist}
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          onAddToProposal={handleAddToProposal}
          onBulkAddToProposal={handleBulkAddToProposal}
          isAuthenticated={!!user}
          currentFilters={{
            genre: advancedFilters.genre?.[0] || '',
            location: advancedFilters.location,
            dateFrom: advancedFilters.dateFrom,
            dateTo: advancedFilters.dateTo,
            artistName: advancedFilters.artistName,
            venueName: advancedFilters.venueName,
          }}
        />

        {/* Unauthenticated User Invitation */}
        {!user && (
          <div className="mt-16 bg-gradient-to-r from-blue-500/5 via-purple-500/5 to-blue-500/5 rounded-2xl p-8 border border-blue-500/10">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full mb-6">
                <Search className="h-8 w-8 text-white" />
              </div>
              <h2 className="text-3xl font-bold text-neutral-900 mb-4">Unlock Advanced Search & Full Functionality</h2>
              <p className="text-lg text-neutral-600 mb-6 max-w-2xl mx-auto">
                Create a <strong>free MAX Platform™ account</strong> to access advanced audience filters, save shows, create proposals, and unlock the full power of our sponsorship discovery tools.
              </p>
              <div className="grid md:grid-cols-3 gap-6 mb-8">
                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-10 h-10 bg-blue-100 rounded-lg mb-3">
                    <Filter className="h-5 w-5 text-blue-600" />
                  </div>
                  <h4 className="font-semibold text-neutral-900 mb-1">Advanced Filters</h4>
                  <p className="text-sm text-neutral-600">Target by demographics, interests, income, and more</p>
                </div>
                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-10 h-10 bg-purple-100 rounded-lg mb-3">
                    <MapPin className="h-5 w-5 text-purple-600" />
                  </div>
                  <h4 className="font-semibold text-neutral-900 mb-1">Save & Organize</h4>
                  <p className="text-sm text-neutral-600">Build your collection of perfect opportunities</p>
                </div>
                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-10 h-10 bg-green-100 rounded-lg mb-3">
                    <Calendar className="h-5 w-5 text-green-600" />
                  </div>
                  <h4 className="font-semibold text-neutral-900 mb-1">Create Plans</h4>
                  <p className="text-sm text-neutral-600">Professional campaigns with AI insights</p>
                </div>
              </div>
              <div className="flex justify-center">
                <Link href="/register">
                  <Button className="btn-max-primary px-8 py-3 text-lg font-semibold hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                    Create Free Account
                  </Button>
                </Link>
              </div>
              <p className="text-sm text-neutral-500 mt-4">
                Already discovering opportunities? <Link href="/login" className="text-max-brand hover:underline font-medium">Sign in here</Link>
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Authentication Required Modal for Persona Builder */}
      {showAuthModal && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[60] p-4"
          onClick={() => {
            setShowAuthModal(false);
            // Clean up URL parameter
            const url = new URL(window.location.href);
            url.searchParams.delete('openPersona');
            window.history.replaceState({}, '', url.pathname + (url.search || ''));
          }}
        >
          <div
            className="bg-white rounded-2xl max-w-2xl w-full shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header with gradient background */}
            <div className="bg-gradient-to-r from-blue-600 via-purple-600 to-blue-600 p-8 text-white relative overflow-hidden">
              <div className="absolute inset-0 bg-grid-white/10"></div>
              <button
                onClick={() => {
                  setShowAuthModal(false);
                  const url = new URL(window.location.href);
                  url.searchParams.delete('openPersona');
                  window.history.replaceState({}, '', url.pathname + (url.search || ''));
                }}
                className="absolute top-4 right-4 rounded-full p-2 hover:bg-white/20 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
              <div className="relative">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl mb-4">
                  <Target className="h-8 w-8" />
                </div>
                <h2 className="text-3xl font-bold mb-2">Browse by Audience</h2>
                <p className="text-blue-100 text-lg">Create custom audience personas to find perfect sponsorship matches</p>
              </div>
            </div>

            {/* Content */}
            <div className="p-8">
              <div className="grid gap-6 mb-8">
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-blue-100 to-blue-50 rounded-xl flex items-center justify-center">
                    <Users className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Target Specific Demographics</h3>
                    <p className="text-gray-600 text-sm">Filter artists by fan age, gender, ethnicity, income levels, and lifestyle attributes</p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-purple-100 to-purple-50 rounded-xl flex items-center justify-center">
                    <Sparkles className="h-6 w-6 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Save Custom Personas</h3>
                    <p className="text-gray-600 text-sm">Create and save multiple audience profiles for different campaigns and brands</p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-green-100 to-green-50 rounded-xl flex items-center justify-center">
                    <TrendingUp className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Match Percentage Scoring</h3>
                    <p className="text-gray-600 text-sm">See how well each artist's audience matches your target demographics</p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-4 mb-6">
                <p className="text-sm text-gray-700">
                  <strong>Sign in or create a free account</strong> to unlock audience filtering and find the perfect sponsorship opportunities for your brand.
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3">
                <Link
                  href={`/login?returnTo=${encodeURIComponent('/persona/new')}`}
                  className="flex-1"
                >
                  <Button className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold py-6">
                    Sign In
                  </Button>
                </Link>
                <Link
                  href={`/register?returnTo=${encodeURIComponent('/persona/new')}`}
                  className="flex-1"
                >
                  <Button className="w-full btn-max-primary font-semibold py-6">
                    Create Free Account
                  </Button>
                </Link>
              </div>

              <Button
                variant="ghost"
                className="w-full mt-3 text-gray-600 hover:text-gray-900"
                onClick={() => {
                  setShowAuthModal(false);
                  const url = new URL(window.location.href);
                  url.searchParams.delete('openPersona');
                  window.history.replaceState({}, '', url.pathname + (url.search || ''));
                }}
              >
                Explore Without Filters
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
    </BrowseLayout>
  );
}